import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {entriesOf, isMultiAccountGroup} from '../clientGroups';

import {ChannelLogo} from '../../channels/ChannelLogo';
import {Check, PencilSimple, Question, Trash} from '../../icons';
import {CLIENT_NAME_MAX_LENGTH} from '../wizardReducer';

/**
 * Un cliente propuesto, con las cuentas que lo componen.
 *
 * El tilde es el ÚNICO mecanismo de inclusión: lo que está tildado se va a crear y lo que no, no.
 * Antes había además un botón de "excluir seleccionadas" y era ambiguo — operaba sobre lo tildado,
 * que es justamente lo que el usuario quiere conservar. Y hasta el 30/09 había otro más: el
 * «+ Incluir igual» / «↺ Deshacer» de la derecha, que llamaba a la MISMA función que la casilla.
 * Leo, en su alta: «el Incluir igual lo sacaría, es poco claro» (card 03NF0The). Queda la casilla,
 * con un cartel que dice qué hace.
 *
 * `onEditorToggle(key, abierto)` avisa cuándo hay un editor abierto (la pantalla congela el orden
 * mientras tanto), `onEditorClosed(key)` cuándo el usuario lo cerró (la pantalla lleva la fila a la
 * vista), y `onRemoved(group, {hadFocus})` que se apretó el tacho (la pantalla ofrece deshacer).
 *
 * **Va memoizada por el buscador.** Cada tecla vuelve a filtrar, pero las filas que siguen entrando
 * son las mismas: medido sobre 638 filas, reusarlas baja el tecleo de **62 ms ±49 a 9 ms ±2**. En
 * «Mostrar más» no cambia nada (131 ±52 contra 120 ±68) porque las cien nuevas hay que montarlas
 * igual — no le atribuyas esa mejora.
 *
 * Depende de que `clientGroupsOf` esté memoizado aguas arriba: si el grupo se reconstruye en cada
 * render, los props cambian de identidad siempre y esto no ahorra nada.
 */
export const ClientProposalRow = React.memo(function ClientProposalRow({group, dispatch, rowErrors,
                                                                       onEditorToggle = null,
                                                                       onEditorClosed = null,
                                                                       onRemoved = null}) {
    const {t} = useTranslation();
    const [editing, setEditing] = useState(false);
    const [open, setOpen] = useState(false);
    const [draftName, setDraftName] = useState(group.name || '');

    // El aviso sale del EFECTO y no del clic: así también se entera la pantalla cuando la fila se
    // desmonta con el editor abierto (un filtro que la esconde, un cliente que se quedó sin cuentas),
    // y el orden no queda congelado para siempre por un editor que ya no existe.
    useEffect(() => {
        if (!open || !onEditorToggle) return undefined;
        onEditorToggle(group.key, true);
        return () => onEditorToggle(group.key, false);
    }, [open, group.key, onEditorToggle]);

    // TODAS las entradas de la fila, cuentas y dudas. Mirando sólo `group.accounts`, una fila
    // cuyas entradas están todas en duda tenía el tilde apagado sin estar apagada, no se leía
    // como omitida, y destildarla despachaba una lista vacía: un control que miente y un gesto
    // que no hace nada, sobre lo que en una cartera de merges es la mayoría de la pantalla.
    const entries = entriesOf(group);
    const anyIncluded = entries.some(entry => entry.decision.include);
    const isOmitted = entries.length > 0 && !anyIncluded;
    // Apagada porque hace meses que no gasta, no porque el usuario la haya destildado.
    const isDormant = isOmitted && entries.every(entry => entry.decision.isDormant);
    // Apagada porque es un MERGE que todavía nadie confirmó. Es el tercer motivo por el que una
    // fila puede estar apagada, y hasta acá se veía idéntico al primero: tachada y con «Deshacer».
    // Con 246 propuestas la mayoría son merges, así que casi toda la pantalla se leía como «esto
    // no lo podés usar» — fede, en la pasada con Pablo: «le da la sensación al usuario de que no la
    // puede usar, lo cual es incorrecto».
    const isUnconfirmed = isOmitted && !isDormant
        && entries.every(entry => entry.decision.unconfirmed);
    // La PASTILLA «sin confirmar» sólo va donde señala un riesgo: un merge que nadie miró.
    //
    // `isUnconfirmed` gobierna el VERBO («Incluir igual» en vez de «Deshacer») y eso vale para
    // cualquier fila que nació apagada — o sea, desde que no se pre-tilda nada, para casi todas.
    // La pastilla no: repetida en las 246 filas de una cartera no distingue nada y convierte la
    // pantalla en una lista de advertencias, que es justo lo que el SCSS de `.ob-tag.-unconfirmed`
    // dice estar evitando. Sobre un cliente de UNA cuenta, además, no hay ningún merge que
    // consentir: lo único que falta ahí es el tilde, y eso ya lo dice el checkbox vacío.
    const showUnconfirmedTag = isUnconfirmed && isMultiAccountGroup(group);
    // Este cliente es uno de los que falló al crear la cartera: se marca para que el usuario sepa
    // dónde mirar, en vez de tener que adivinar cuál de sus doce filas rebotó.
    const rowErrorsOfGroup = entries.flatMap(entry => (rowErrors || {})[entry.pk] || []);
    const hasError = rowErrorsOfGroup.length > 0;
    const errorCodes = Array.from(new Set(rowErrorsOfGroup.map(error => error.code)));

    // Un gesto, una acción. Con un dispatch por cuenta —como estaba— tildar la fila de un cliente de
    // ocho cuentas eran ocho reducciones y ocho renders; el mismo patrón aplicado a la lista entera
    // no escala a las seiscientas filas que tiene una cartera grande.
    const toggleAll = () => {
        dispatch({type: 'SET_INCLUDE_MANY', pks: entries.map(entry => entry.pk),
                  include: !anyIncluded});
    };

    const closeEditor = () => {
        setOpen(false);
        if (onEditorClosed) onEditorClosed(group.key);
    };

    /**
     * «Listo, crear este cliente»: cierra el editor Y deja el cliente elegido.
     *
     * Antes sólo cerraba, y Leo editó Bakián, tocó «Listo» y la fila siguió sin tildar — «Listos
     * para crear 0», el botón en 0. «Una vez que pongo Listo, que sea listo para crear» (D3). Se
     * tilda sobre las entradas de AHORA, las que quedaron después de sacar o separar.
     */
    const finishEditing = () => {
        const pks = entries.map(entry => entry.pk);
        if (pks.length) dispatch({type: 'SET_INCLUDE_MANY', pks, include: true});
        closeEditor();
    };

    /**
     * Eliminar la propuesta, que NO es lo mismo que destildarla.
     *
     * Destildada, la fila se queda: tachada, contando entre las que el usuario todavía puede
     * reconsiderar. Eso está bien sobre veinte filas y es un estorbo sobre seiscientas, donde lo que
     * uno descartó vuelve a pasar por delante en cada barrido. Eliminar la saca de la lista y la deja
     * contada abajo, con su deshacer — el mismo trato que ya recibían las cuentas del cajón.
     *
     * Se lleva también las dudas del grupo: son cuentas de este cliente esperando una respuesta, y
     * dejarlas sueltas preguntando por un cliente que ya no está en pantalla no tiene sentido.
     */
    const remove = event => {
        const pks = group.accounts.concat(group.doubts).map(entry => entry.pk);
        // Si el foco estaba en ESTE tacho, la pantalla lo lleva a otro lado: la fila desaparece, la
        // de abajo sube, y el foco no puede quedar en el aire ni aterrizar en el tacho siguiente.
        const hadFocus = Boolean(event && event.currentTarget === document.activeElement);
        dispatch({type: 'SET_DISMISSED_MANY', dismissed: true, pks});
        if (onRemoved) onRemoved(group, {pks, hadFocus});
    };

    const commitRename = () => {
        setEditing(false);
        const trimmed = draftName.trim();
        if (trimmed && trimmed !== group.name && group.clientRef?.type === 'new') {
            dispatch({type: 'RENAME_CLIENT', originalName: group.clientRef.name, newName: trimmed});
        }
    };

    return (
        <div className={`ob-prop${isOmitted ? ' -omitted' : ''}${isDormant ? ' -dormant' : ''}`
                        + `${isUnconfirmed ? ' -unconfirmed' : ''}${hasError ? ' -error' : ''}`}
             data-group-key={group.key}>
            <div className="ob-prop__main">
                {/* El cartel dice qué pasa al apretar, no el estado: el estado ya lo dice la casilla.
                    Es la pista que le faltó a Leo para entender que ésta era LA forma de elegir
                    («pondría algo que diga que está validado»). */}
                <span className="ob-check-wrap">
                    <button type="button" role="checkbox" aria-checked={anyIncluded}
                            className={`ob-check${anyIncluded ? ' -on' : ''}`} onClick={toggleAll}
                            aria-label={t('onboarding.clients.include', {client: group.name,
                                                                         interpolation: {escapeValue: false}})}>
                        {anyIncluded && <Check size={13} weight="bold"/>}
                    </button>
                    <span className="ob-tip" aria-hidden="true">
                        {anyIncluded ? t('onboarding.clients.tick_skip') : t('onboarding.clients.tick_create')}
                    </span>
                </span>

                <div className="ob-prop__name">
                    {editing ? (
                        // El backend rechaza los nombres de más de 50: cortarlo acá evita el viaje
                        // de ida y vuelta para descubrirlo. El número sale del reducer, que es
                        // donde vive el largo que acepta `AgencyClient._name`.
                        <input autoFocus value={draftName} maxLength={CLIENT_NAME_MAX_LENGTH}
                               onChange={event => setDraftName(event.target.value)}
                               onBlur={commitRename}
                               onKeyDown={event => {
                                   if (event.key === 'Enter') commitRename();
                                   if (event.key === 'Escape') {
                                       setDraftName(group.name || '');
                                       setEditing(false);
                                   }
                               }}
                               aria-label={t('onboarding.clients.rename')}/>
                    ) : (
                        <>
                            <span className="ob-prop__title">{group.name}</span>
                            {group.isNew && (
                                <button type="button" className="ob-prop__edit" onClick={() => setEditing(true)}
                                        aria-label={t('onboarding.clients.rename')}>
                                    <PencilSimple size={13}/>
                                </button>
                            )}
                            {!group.isNew && (
                                <span className="ob-tag">{t('onboarding.clients.already_exists')}</span>
                            )}
                            {/* Una cuenta dormida ya vinculada a su cliente llega apagada por
                                decisión nuestra —una línea vigila un presupuesto que se gasta— y se
                                veía EXACTAMENTE igual que una que el usuario destildó: tachada, con
                                un «Deshacer» que le proponía revertir algo que no había hecho. Las
                                sin dueño ya explican esto en su sección del cajón; ésta no pasa por
                                ahí, así que lo dice en su fila. */}
                            {isDormant && (
                                <span className="ob-tag -dormant">{t('onboarding.clients.dormant_tag')}</span>
                            )}
                            {showUnconfirmedTag && (
                                <span className="ob-tag -unconfirmed">
                                    {t('onboarding.clients.unconfirmed_tag')}
                                </span>
                            )}
                        </>
                    )}
                </div>

                <div className="ob-prop__accounts">
                    {group.accounts.map(entry => (
                        <AccountChip key={entry.pk} account={entry.account}/>
                    ))}
                </div>

                <div className="ob-prop__actions">
                    <span className="ob-prop__count">
                        {t('onboarding.clients.account_count', {count: group.accounts.length})}
                    </span>
                    {/* «Editar», que es como lo nombró la recorrida del mockup: acá adentro está
                        todo lo que se puede hacer con ESTE cliente. El verbo anterior era
                        «Separar», y nadie supo qué iba a pasar al apretarlo.

                        Con el editor abierto se queda aunque el cliente se haya quedado sin
                        cuentas (sólo con dudas): es la salida sin decidir, y sin él la única forma
                        de cerrar era «Listo, crear este cliente», que tilda las dudas sin
                        responderlas (review C-paso3-04). */}
                    {(open || group.accounts.length > 0) && (
                        <button type="button" className={`ob-prop__act${open ? ' -on' : ''}`}
                                aria-expanded={open} onClick={() => (open ? closeEditor() : setOpen(true))}>
                            <PencilSimple size={13}/> {t('onboarding.clients.edit_group')}
                        </button>
                    )}
                    <button type="button" className="ob-prop__act -remove" onClick={remove}
                            aria-label={t('onboarding.clients.remove_client', {client: group.name,
                                                                               interpolation: {escapeValue: false}})}>
                        <Trash size={13}/>
                    </button>
                </div>
            </div>

            {/* El banner de arriba lista los motivos de TODA la tanda; acá va el de esta fila. Con
                dos causas distintas conviviendo —el tope del plan y una cuenta dada de baja— saber
                cuál le tocó a cada cliente es lo que decide si se saca o se arregla. El texto sale
                del diccionario por código, igual que el del banner: el mensaje del backend no se
                muestra nunca. */}
            {hasError && errorCodes.length > 0 && (
                <p className="ob-prop__error">
                    {errorCodes.map(code => t(`onboarding.apply_error.reason.${String(code).toLowerCase()}`,
                                              t('onboarding.apply_error.reason.generic'))).join(' ')}
                </p>
            )}

            {open && <GroupEditor group={group} dispatch={dispatch} onDone={finishEditing}
                                  onTouch={() => onEditorToggle && onEditorToggle(group.key, true)}/>}

            {group.doubts.map(entry => (
                <DoubtRow key={entry.pk} entry={entry} clientName={group.name} clientRef={group.clientRef}
                          dispatch={dispatch}/>
            ))}
        </div>
    );
});

/**
 * Qué se puede hacer con las cuentas de ESTE cliente.
 *
 * Es el gesto fino que faltaba. El toggle de arriba separa el padrón entero —una decisión de dos
 * mundos: «mi agencia tiene una cuenta por cliente» o «tiene varias»— y sirve para eso. Pero cuando
 * la agrupación acierta en doscientas filas y falla en una, no había ninguna herramienta a la escala
 * del error: la única salida era desarmar todo y rehacerlo a mano.
 *
 * Las dos salidas por cuenta son las dos formas reales en que un merge está mal: la cuenta es de OTRO
 * negocio (se separa y se queda con su propio cliente) o no se sabe de quién es (se saca y baja al
 * cajón, que es donde se asigna). No hay una tercera, y por eso no hay un menú.
 *
 * El botón de cierre es «Listo, crear este cliente» y hace las dos cosas que dice (ver
 * `finishEditing`). Cerrar sin elegir sigue existiendo: es el mismo «Editar» que lo abrió.
 *
 * `onTouch` avisa ANTES de cada gesto que esta es la fila en la que se está trabajando: con dos
 * editores abiertos, el cliente que nace de «Separar» se ubica debajo de ésta y no de la otra.
 */
function GroupEditor({group, dispatch, onDone, onTouch}) {
    const {t} = useTranslation();
    const canSplit = group.accounts.length > 1;
    const act = action => {
        onTouch();
        dispatch(action);
    };

    return (
        <div className="ob-editor">
            <p className="ob-editor__hint">
                {canSplit ? t('onboarding.clients.edit_hint') : t('onboarding.clients.edit_hint_single')}
            </p>
            {group.accounts.map(entry => (
                <div className="ob-editor__row" key={entry.pk}>
                    <AccountChip account={entry.account}/>
                    <div className="ob-editor__actions">
                        {canSplit && (
                            <button type="button" className="ob-btn -mini -ghost"
                                    onClick={() => act({type: 'SPLIT_ACCOUNT', pk: entry.pk,
                                                        fallbackName: entry.account.name})}>
                                {t('onboarding.clients.split_out')}
                            </button>
                        )}
                        <button type="button" className="ob-link -muted"
                                onClick={() => act({type: 'UNASSIGN_ACCOUNT', pk: entry.pk})}>
                            {t('onboarding.clients.unassign')}
                        </button>
                    </div>
                </div>
            ))}
            <button type="button" className="ob-btn -mini -primary ob-editor__close" onClick={onDone}>
                <Check size={13} weight="bold"/> {t('onboarding.clients.edit_done')}
            </button>
        </div>
    );
}

export function AccountChip({account}) {
    // El nombre completo va en el chip y se recorta con CSS: nada de `title` nativo (el diseño del
    // producto usa su propio tooltip, y el del navegador tarda un segundo en aparecer y no se puede
    // leer con el teclado).
    const label = account.name || String(account.account_id);
    return (
        <span className="ob-account-chip">
            <ChannelLogo channel={account.channel} hspace={0} vspace={0} marginLeft={0} padding={0}/>
            <span>{label}</span>
        </span>
    );
}

/**
 * La pregunta que el sistema hace cuando no está seguro.
 *
 * Va debajo del cliente al que la cuenta se sumaría, en ámbar y con las dos respuestas a la vista.
 * Responderla no frena el resto: se puede llegar al final del recorrido con dudas sin contestar, y
 * esas cuentas simplemente quedan sin asignar.
 */
export function DoubtRow({entry, clientName, clientRef, dispatch}) {
    const {t} = useTranslation();
    const {account} = entry;

    return (
        <div className="ob-doubt" role="group">
            <Question size={15} className="ob-doubt__icon"/>
            <AccountChip account={account}/>
            <p className="ob-doubt__question">
                {t('onboarding.clients.doubt', {account: account.name || account.account_id, client: clientName,
                                                interpolation: {escapeValue: false}})}
            </p>
            <div className="ob-doubt__actions">
                <button type="button" className="ob-btn -mini -primary"
                        onClick={() => dispatch({type: 'ANSWER_DOUBT', pk: entry.pk, belongs: true, clientRef})}>
                    {t('onboarding.clients.doubt_yes')}
                </button>
                <button type="button" className="ob-btn -mini -ghost"
                        onClick={() => dispatch({type: 'ANSWER_DOUBT', pk: entry.pk, belongs: false, clientRef})}>
                    {t('onboarding.clients.doubt_no')}
                </button>
            </div>
        </div>
    );
}
